import React, { useState, useEffect } from 'react';
import { fetchPurchases, savePurchase, fetchProducts, fetchCustomers, deletePurchase } from '../../services/api';

const now = new Date();
const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

const formatDateForAPI = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-") && dateStr.split("-")[0].length === 4) {
    // YYYY-MM-DD to DD-MM-YYYY
    const [y, m, d] = dateStr.split("-");
    return `${d}-${m}-${y}`;
  }
  return dateStr;
};

const formatDateForUI = (dateStr) => {
  if (!dateStr) return "";
  if (dateStr.includes("-") && dateStr.split("-")[0].length <= 2) {
    // DD-MM-YYYY to YYYY-MM-DD
    const [d, m, y] = dateStr.split("-");
    return `${y}-${m}-${d}`;
  }
  return dateStr;
};

const getTodayForUI = () => {
  const d = new Date();
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  let year = d.getFullYear();
  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;
  return [year, month, day].join('-');
};

export default function PurchaseEntry({ user }) {
  const [entries, setEntries] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [filterSupplier, setFilterSupplier] = useState("");
  
  const companyId = user?.Companyid || user?.company_id || "1";
  
  const [form, setForm] = useState({ 
    date: '', 
    poNo: '', 
    supplier: '', 
    product: '', 
    qty: '', 
    rate: '', 
    taxPer: 0, 
    invoiceNo: '',
    paymentMode: ''
  });

  useEffect(() => {
    loadInitialData();
  }, [companyId]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Fetch Purchases
      const pRes = await fetchPurchases({
        Companyid: companyId,
        Name: filterSupplier,
        Product: "",
        FromDate: formatDateForAPI(dateRange.from),
        ToDate: formatDateForAPI(dateRange.to)
      });
      if (pRes.success) {
        const raw = pRes.Response;
        const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
        setEntries(list);
      }

      // 2. Fetch Products
      const prodRes = await fetchProducts(companyId);
      if (prodRes.success) setProducts(prodRes.Response || []);

      // 3. Fetch Suppliers (using fetchCustomers as per system pattern)
      const suppRes = await fetchCustomers(companyId);
      if (suppRes.success) {
        const allSupp = suppRes.Response || [];
        // Filter only Party or Suppliers
        setSuppliers(allSupp.filter(s => 
          s.Type === "Party" || s.Type === "Suppliers" || s.Type === "Supplier"
        ));
      }

    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const calcAmount = (f) => {
    const qty = parseFloat(f.qty) || 0;
    const rate = parseFloat(f.rate) || 0;
    const taxPer = parseFloat(f.taxPer) || 0;
    const amount = qty * rate;
    const taxAmount = (amount * taxPer) / 100;
    return {
      amount,
      taxAmount,
      total: amount + taxAmount
    };
  };

  const handleSave = async () => {
    if (!form.date || !form.qty || !form.rate || !form.supplier || !form.paymentMode || !form.product) {
      alert("Please fill all required fields, including Date, Product and Payment Mode");
      return;
    }

    const { amount, taxAmount, total } = calcAmount(form);

    const apiData = {
      Companyid: companyId,
      Name: form.supplier,
      Product: form.product,
      Qty: form.qty.toString(),
      Rate: form.rate.toString(),
      Amount: amount.toFixed(2),
      Tax: form.taxPer.toString(),
      TaxAmount: taxAmount.toFixed(2),
      Total: total.toFixed(2),
      Date1: formatDateForAPI(form.date),
      PaymentMode: form.paymentMode,
      BillNo: form.invoiceNo || form.poNo,
      IDS: editId ? String(editId) : "0"
    };

    setLoading(true);
    try {
      const res = await savePurchase(apiData);
      if (res.success || res.Response === "UPDATED" || res.Response === "INSERTED") {
        alert(res.message || "Purchase saved successfully!");
        loadInitialData();
        setShowForm(false);
        setForm({ 
          date: '', poNo: '', supplier: '', product: '', 
          qty: '', rate: '', taxPer: 0, 
          invoiceNo: '', paymentMode: '' 
        });
      } else {
        alert(res.message || "Failed to save purchase");
      }
    } catch (err) {
      console.error('Save error:', err);
      alert("Error saving record");
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (e) => {
    // Strictly map IDS to ID as requested
    const purchaseId = e.ID || e.IDS || e.id || "0";
    setEditId(purchaseId);
    setForm({
      date: formatDateForUI(e.Date1 || e.date || getTodayForUI()),
      poNo: e.BillNo || e.billNo || '',
      supplier: e.Customer || e.supplier || e.Name || '',
      product: e.Product || e.product || 'Petrol',
      qty: e.Qty || e.qty || '',
      rate: e.Rate || e.rate || '',
      taxPer: e.Tax || e.tax || 0,
      invoiceNo: e.BillNo || e.billNo || '',
      paymentMode: e.PaymentMode || e.payment_mode || 'Credit'
    });
    setShowForm(true);
  };

  const handleDelete = async (e) => {
    const id = e.ID || e.IDS || e.id;
    if (!id) return;
    if (!window.confirm("Are you sure you want to delete this purchase entry?")) return;

    setLoading(true);
    try {
      const result = await deletePurchase(id);
      if (result.success || result.Response === "DELETED" || result.status === "Success") {
        await loadInitialData();
      } else {
        alert(result.message || "Failed to delete purchase");
      }
    } catch (err) {
      console.error("Delete purchase error:", err);
    } finally {
      setLoading(false);
    }
  };
 
  const openAdd = () => {
    setEditId(null);
    setForm({ 
      date: '', poNo: '', supplier: '', product: '', 
      qty: '', rate: '', taxPer: 0, 
      invoiceNo: '', paymentMode: '' 
    });
    setShowForm(true);
  };

  if (loading && entries.length === 0) return <div style={{padding: 20}}>Loading...</div>;

  return (
    <div className="pump-page">
      <div className="page-header">
        <h2 className="page-title">Purchase Entry</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          
          <select 
            className="form-control form-control-sm"
            style={{ width: 150 }}
            value={filterSupplier}
            onChange={e => setFilterSupplier(e.target.value)}
          >
            <option value="">All Suppliers</option>
            {suppliers.map(s => (
              <option key={s.Customerid || s.id} value={s.Name || s.name}>
                {s.Name || s.name}
              </option>
            ))}
          </select>

          <div className="date-filter" style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border)' }}>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              style={{ width: 130, border: 'none', padding: 0 }}
              value={dateRange.from}
              onChange={e => setDateRange({...dateRange, from: e.target.value})}
            />
            <span>to</span>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              style={{ width: 130, border: 'none', padding: 0 }}
              value={dateRange.to}
              onChange={e => setDateRange({...dateRange, to: e.target.value})}
            />
          </div>
          <button className="btn btn-secondary btn-sm" onClick={loadInitialData} title="Refresh">
            <i className="bi bi-arrow-clockwise"></i>
          </button>
          <button className="btn btn-primary btn-sm" onClick={openAdd}>
            ＋ New Entry
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 12, marginBottom: 14 }}>
        {[
          { label: 'Total Entries', val: entries.length, color: 'var(--primary)' },
          { label: 'Total Value', val: '₹' + entries.reduce((s, e) => s + parseFloat(e.Total || e.amount || 0), 0).toLocaleString(), color: 'var(--info)' },
        ].map(c => (
          <div key={c.label} style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', boxShadow: 'var(--shadow-sm)', borderLeft: `4px solid ${c.color}` }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.val}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 600 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? 'Edit Purchase Entry' : 'New Purchase Entry'}</div>
          <div className="form-row">
            <div className="form-group"><label>Date</label><input type="date" className="form-control" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group"><label>Invoice / Bill No.</label><input className="form-control" value={form.invoiceNo} onChange={e => setForm({ ...form, invoiceNo: e.target.value })} placeholder="INV-XXXX" /></div>
            <div className="form-group">
              <label>Supplier</label>
              <select className="form-control" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })}>
                <option value="">Select Supplier</option>
                {suppliers.map(s => (
                  <option key={s.Customerid || s.id} value={s.Name || s.name}>{s.Name || s.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Product</label>
              <select className="form-control" value={form.product} onChange={e => setForm({ ...form, product: e.target.value })}>
                <option value="">Select Product</option>
                {products.map(p => (
                  <option key={p.Productid || p.id} value={p.Name || p.name}>{p.Name || p.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group"><label>Quantity</label><input type="number" className="form-control" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} /></div>
            <div className="form-group"><label>Rate (₹)</label><input type="number" className="form-control" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} /></div>
            <div className="form-group"><label>Tax %</label><input type="number" className="form-control" value={form.taxPer} onChange={e => setForm({ ...form, taxPer: e.target.value })} /></div>
            <div className="form-group">
              <label>Payment Mode</label>
              <select className="form-control" value={form.paymentMode} onChange={e => setForm({ ...form, paymentMode: e.target.value })}>
                <option value="">Select Payment Mode</option>
                <option>Credit</option>
                <option>Cash</option>
                <option>UPI</option>
              </select>
            </div>

            
            {form.qty && form.rate && (
              <div className="form-group balance-group">
                <label>Total Amount (Inc. Tax)</label>
                <div className="balance-value" style={{ color: 'var(--secondary)', background: '#f0fff4', borderColor: '#9ae6b4' }}>
                  ₹{calcAmount(form).total.toLocaleString()}
                </div>
              </div>
            )}
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
              <i className="bi bi-save me-1"></i> 
              {loading 
                ? (editId ? 'Updating...' : 'Saving...') 
                : (editId ? 'Update' : 'Save')}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>
              <i className="bi bi-x-lg me-1"></i> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="section-card">
        <div className="section-header">Purchase History</div>
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-center">Date</th>
                <th className="text-center">Bill No.</th>
                <th className="text-left">Supplier</th>
                <th className="text-left">Product</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Rate (₹)</th>
                <th className="text-right">Tax %</th>
                <th className="text-right">Tax Amt</th>
                <th className="text-right">Total (₹)</th>
                <th className="text-center">Mode</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr><td colSpan="11" className="text-center" style={{ padding: 20 }}>No records found</td></tr>
              ) : (
                entries.map((e, i) => (
                  <tr key={i}>
                    <td className="text-center">{e.Date1 || e.date}</td>
                    <td className="text-center" style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>{e.BillNo || e.billNo || '—'}</td>
                    <td className="text-left" style={{ fontWeight: 600 }}>{e.Customer || e.supplier || e.Name}</td>
                    <td className="text-left"><span className="badge badge-info">{e.Product || e.product}</span></td>
                    <td className="text-right">{parseFloat(e.Qty || e.qty || 0).toFixed(3)}</td>
                    <td className="text-right">₹{parseFloat(e.Rate || e.rate || 0).toFixed(2)}</td>
                    <td className="text-right">{e.Tax || e.tax || 0}%</td>
                    <td className="text-right">₹{parseFloat(e.TaxAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="text-right" style={{ fontWeight: 700 }}>₹{parseFloat(e.Total || e.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="text-center">
                      <span className={`badge ${(e.PaymentMode || '').toUpperCase() === 'CASH' ? 'badge-success' : 'badge-warning'}`}>
                        {e.PaymentMode || 'Credit'}
                      </span>
                    </td>
                    <td className="text-center">
                     <button className="icon-btn" onClick={() => openEdit(e)} title="Edit">
                        <i className="bi bi-pencil-square"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
